import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SERVICES, waLink } from "../data";

const WhatsAppGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export const Services = () => {
  const [active, setActive] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [selected, setSelected] = useState(null);

  const onMove = (e) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    setPos({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
  };

  const s = selected !== null ? SERVICES[selected] : null;

  return (
    <section id="services" className="relative bg-[#FCFBF8] py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-[1500px] px-6 sm:px-12 lg:px-16">
        <div className="mb-14 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-14 bg-[#C5A059]" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.3em] text-[#A37E3A]">
                What We Do
              </span>
            </div>
            <h2 className="font-heading text-4xl font-light leading-[1.05] tracking-tight text-[#1A1918] sm:text-5xl lg:text-6xl">
              Six crafts, <span className="italic text-gradient-gold">one</span> seamless celebration.
            </h2>
          </div>
          <p className="font-body text-base leading-relaxed text-[#5C5A56] lg:col-span-4">
            Tap any service to see photos, a quick intro and message us on WhatsApp.
          </p>
        </div>

        {/* Interactive list */}
        <div className="relative" onMouseMove={onMove} data-testid="services-list">
          {SERVICES.map((svc, i) => (
            <motion.button
              key={svc.no}
              type="button"
              data-testid={`service-row-${svc.no}`}
              onClick={() => setSelected(i)}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="group grid w-full grid-cols-12 items-center gap-4 border-t border-[#E6E2D6] py-7 text-left last:border-b"
            >
              <span className="col-span-2 font-body text-sm text-[#A37E3A] sm:col-span-1">{svc.no}</span>
              <h3
                className="col-span-8 font-heading text-3xl font-light tracking-tight transition-[color,transform] duration-500 sm:col-span-9 sm:text-4xl lg:text-5xl lg:group-hover:translate-x-4"
                style={{ color: active === i ? "#A37E3A" : "#1A1918" }}
              >
                {svc.title}
              </h3>
              <span className="col-span-2 flex justify-end sm:col-span-2">
                <ArrowUpRight
                  className="h-7 w-7 text-[#1A1918] transition-transform duration-500 group-hover:rotate-45 group-hover:text-[#C5A059]"
                  strokeWidth={1.3}
                />
              </span>
              {/* Mobile preview thumb + short desc */}
              <div className="col-span-12 mt-4 flex items-center gap-4 lg:hidden">
                <img
                  src={svc.images[0]}
                  alt={svc.title}
                  loading="lazy"
                  className="h-20 w-24 shrink-0 rounded-xl object-cover"
                />
                <p className="font-body text-sm leading-relaxed text-[#5C5A56]">{svc.desc}</p>
              </div>
            </motion.button>
          ))}

          {/* Floating cursor image (desktop hover) */}
          <AnimatePresence>
            {active !== null && (
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                style={{ left: pos.x, top: pos.y }}
                className="pointer-events-none absolute z-20 hidden -translate-x-1/2 -translate-y-1/2 lg:block"
              >
                <div className="h-72 w-56 overflow-hidden rounded-[999px_999px_16px_16px] border border-[#C5A059]/40 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
                  <img
                    src={SERVICES[active].image}
                    alt={SERVICES[active].title}
                    className="h-full w-full object-cover"
                  />
                </div>
                <p className="mt-3 max-w-[14rem] text-center font-body text-xs leading-relaxed text-[#5C5A56]">
                  Tap to view details
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Service detail popup */}
      <Dialog open={selected !== null} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          data-testid="service-dialog"
          className="max-h-[92vh] gap-0 overflow-y-auto rounded-3xl border-[#E6E2D6] bg-[#FCFBF8] p-0 sm:max-w-lg [&>button:last-child]:hidden"
        >
          {s && (
            <>
              <DialogTitle className="sr-only">{s.title}</DialogTitle>
              <DialogDescription className="sr-only">{s.intro}</DialogDescription>
              <button
                onClick={() => setSelected(null)}
                data-testid="service-dialog-close"
                aria-label="Close"
                className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md transition-colors hover:bg-black/60"
              >
                <X size={16} />
              </button>

              {/* Images */}
              <div className="grid grid-cols-2 gap-1">
                <div className="relative col-span-2 h-52 overflow-hidden sm:h-60">
                  <img src={s.images[0]} alt={s.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute bottom-3 left-4 font-body text-[10px] uppercase tracking-[0.3em] text-[#EAD39C]">
                    {s.no} · Golden Decor
                  </span>
                </div>
                {s.images.slice(1).map((im, k) => (
                  <div
                    key={k}
                    className={`overflow-hidden ${s.images.length - 1 === 1 ? "col-span-2 h-32 sm:h-36" : "col-span-1 h-28 sm:h-32"}`}
                  >
                    <img src={im} alt={s.title} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                ))}
              </div>

              {/* Body */}
              <div className="p-6 sm:p-8">
                <h3
                  className="font-heading text-3xl font-light leading-tight text-[#1A1918] sm:text-4xl"
                  data-testid="service-dialog-title"
                >
                  {s.title}
                </h3>
                <div className="mt-3 h-px w-16" style={{ backgroundColor: s.accent }} />
                <p className="mt-5 font-body text-base leading-relaxed text-[#5C5A56]">{s.intro}</p>

                <a
                  href={waLink(s.waText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-dialog-whatsapp"
                  className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] px-6 py-4 font-body text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02]"
                >
                  <WhatsAppGlyph className="h-5 w-5" />
                  Enquire on WhatsApp
                </a>
                <p className="mt-3 text-center font-body text-xs text-[#8a877f]">
                  We usually reply within a few minutes
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
